import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { rollbar } from '../../lib/rollbar';

// Default fallback shown when a route loader throws. Wired up via the
// router's `defaultErrorComponent`; routes can override per-route.
//
// Retrying goes through `router.invalidate()` rather than the `reset` the router hands us: `reset` only clears
// the catch boundary's own state, and the match still holds its error, so it rethrows on the next render.
//
// Distinct from `ErrorFallback`, which catches React render errors at the
// app root. This one only handles loader/route failures.
export function RouteErrorFallback({ error }: ErrorComponentProps): ReactElement {
    const { t } = useTranslation();
    const router = useRouter();

    useEffect(() => {
        rollbar.error('Route loader failed', error instanceof Error ? error : new Error(String(error)));
    }, [error]);

    return (
        <Box sx={{ maxWidth: 480, mx: 'auto', py: 6, px: 3 }}>
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => void router.invalidate()}>
                        {t('ACTIONS.TRY_AGAIN')}
                    </Button>
                }>
                {import.meta.env.DEV && error instanceof Error ? error.message : t('ERRORS.ROUTE_LOAD_FAILED')}
            </Alert>
        </Box>
    );
}
